import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TRIAL_PATHS } from "../saas/trial/contract";

let _cache = null;

/**
 * usePlatformConfig — fetches `GET /api/saas/config` once per session (module-cached, the
 * same shape as `useSiteContent`) and exposes the platform-wide facts the SaaS
 * marketing/signup lane needs so a locale string never has to spell them itself:
 *
 *   - `trialPeriodDays`: the trial length Stripe was told (`SAAS_TRIAL_PERIOD_DAYS`, mirrored
 *     server-side). Marketing/signup copy interpolates `{days}` from this instead of a "30"
 *     baked into fr/en/es strings, which is exactly the drift class
 *     `saas/__tests__/landingPricingFacts.test.js` (and its trial sibling) exist to catch.
 *   - `stripeDemoMode`: unchanged meaning from the config Signup.jsx used to fetch locally
 *     (`config.stripe_demo_mode`).
 *
 * Nothing shared existed before this hook: every caller fetched `/saas/config` on its own
 * (`Signup.jsx:65-69`), so a second consumer (the landing page, the success screen) had no
 * way to agree with the first on what the server actually said.
 *
 * `trialPeriodDays` is `null` until the config resolves, or if the server ever omits or
 * mistypes the field — callers must render the digit-free generic sentence in that case,
 * never an interpolated "undefined-day trial".
 */
export default function usePlatformConfig() {
  const [config, setConfig] = useState(_cache || { trialPeriodDays: null, stripeDemoMode: false });

  useEffect(() => {
    if (_cache) return;
    api
      .get(TRIAL_PATHS.saasConfig)
      .then(({ data }) => {
        const days = data?.trial_period_days;
        _cache = {
          trialPeriodDays: Number.isInteger(days) ? days : null,
          stripeDemoMode: Boolean(data?.stripe_demo_mode),
        };
        setConfig(_cache);
      })
      .catch(() => {});
  }, []);

  return config;
}

/** Force a reload of the cached config — used after a super-admin change, and by tests. */
export function invalidatePlatformConfig() {
  _cache = null;
}
